import { PrimaryButton, Spinner, Text, TextField, Toggle, SelectionMode } from '@fluentui/react';
import useAxios from 'axios-hooks';
import { Formik, Form } from 'formik';
import { useMemo, useState } from 'react';
import Select from 'react-select';
import * as Yup from 'yup';
import SuccessComponent from '../../../Util/SuccessComponent';
import ErrorComponent from '../../../Util/ErrorComponent';
import DetailsTable from '../../../Table/DetailsTable';
import { FixedSizeList as List } from 'react-window';

const validationSchema = Yup.object().shape({
  cobrandId: Yup.number().nullable().required('Cobrand is required'),
  clientTenantId: Yup.string().when('teamsIsSetup', {
    is: false,
    then: (schema) => schema.required('Client Tenant ID is required when Teams is not set up'),
    otherwise: (schema) => schema.nullable(),
  }),
  cobrandBaseUrl: Yup.string()
    .max(255, 'Base URL must be 255 characters or less')
    .nullable(),
  cobrandTeamsDescription: Yup.string().max(255, 'Teams Description must be 255 characters or less').nullable(),
});

const initialValues = {
  cobrandId: null,
  clientTenantId: '',
  cobrandBaseUrl: '',
  cobrandTeamsDescription: '',
  active: false,
  teamsIsSetup: false,
};

const CustomMenuList = ({ children, ...props }) => {
  const height = 35;
  const itemCount = Array.isArray(children) ? children.length : 0;
  const totalHeight = itemCount > 0 ? Math.min(300, itemCount * height) : height;

  if (itemCount === 0) {
    return null;
  }

  return (
    <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
      {({ index, style }) => (
        <div
          style={{
            ...style,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: '0 8px',
            boxSizing: 'border-box',
          }}
          title={children[index]?.props?.children}
        >
          {children[index]}
        </div>
      )}
    </List>
  );
};
const TeamsSettings = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userEmailError, setUserEmailError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [{ data: cobrands, loading: loadingCobrands }] = useAxios('/internal/cobrands');
  const cobrandsOptions = useMemo(
    () => cobrands?.map((u) => ({ value: u.cobrandId, label: u.name })),
    [cobrands]
  );

  const [{ data: teamsData, loading: loadingTeams }, fetchTeams] = useAxios(
    { url: '/teams/admin-settings', method: 'GET' },
    { manual: true }
  );

  const [{ data, loading: submitting }, submitForm] = useAxios(
    { url: '/teams/admin-settings' },
    { manual: true }
  );

  const [{ data: msTeamsMessages, loading: loadingMessages }] = useAxios('/teams/messages');

  const [{ data: userMessageLogs, loading: loadingUserMessages }, fetchUserMessages] = useAxios(
    { url: '/teams/user-message-logs', method: 'GET' },
    { manual: true }
  );

  const messagesColumns = useMemo(() => [
    {
      key: 'title',
      name: 'Title',
      fieldName: 'title',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: 'description',
      name: 'Description',
      fieldName: 'description',
      minWidth: 300,
      maxWidth: 500,
      isResizable: true,
    },
    {
      key: 'orderNumber',
      name: 'Order Number',
      fieldName: 'orderNumber',
      minWidth: 100,
      maxWidth: 120,
      isResizable: true,
    },
  ], []);

  const userMessageColumns = useMemo(() => [
    {
      key: 'title',
      name: 'Title',
      fieldName: 'title',
      minWidth: 200,
      maxWidth: 400,
      isResizable: true,
    },
    {
      key: 'orderNumber',
      name: 'Order Number',
      fieldName: 'orderNumber',
      minWidth: 100,
      maxWidth: 120,
      isResizable: true,
    },
  ], []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (newValue) => {
    const email = newValue || '';
    setUserEmail(email);
    setHasSearched(false);

    if (email && !validateEmail(email)) {
      setUserEmailError('Please enter a valid email address');
    } else {
      setUserEmailError('');
    }
  };

  const handleSearchUserMessages = async (cobrandId) => {
    if (!userEmail || !cobrandId) return;

    if (!validateEmail(userEmail)) {
      setUserEmailError('Please enter a valid email address');
      return;
    }

    setHasSearched(true);

    try {
      const result = await fetchUserMessages({
        url: `/teams/user-message-logs?cobrandId=${cobrandId}&email=${encodeURIComponent(userEmail)}`
      });
      if (result.error) {
        setShowError(true);
      }
    } catch (error) {
      setShowError(true);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setFieldValue }) => {
          const result = await submitForm({
            url: '/teams/admin-settings',
            method: 'POST',
            data: {
              CobrandId: values.cobrandId,
              ClientTenantId: values.clientTenantId || null,
              CobrandBaseUrl: values.cobrandBaseUrl || null,
              CobrandTeamsDescription: values.cobrandTeamsDescription || null,
              Active: values.active,
            },
          });
          if (!result.isAxiosError) {
            setShowSuccess(true);
            setIsFormDirty(false);

            try {
              const refreshResult = await fetchTeams({ url: `/teams/admin-settings?cobrandId=${values.cobrandId}` });
              if (!refreshResult.error) {
                const settings = refreshResult.data || {};
                setFieldValue('cobrandBaseUrl', settings.cobrandBaseUrl ?? '');
                setFieldValue('cobrandTeamsDescription', settings.cobrandTeamsDescription ?? '');
                setFieldValue('active', settings.active ?? false);
                setFieldValue('teamsIsSetup', settings.teamsIsSetup ?? false);
              }
            } catch (refreshError) {
              console.warn('Failed to refresh Teams data after successful submission:', refreshError);
            }
          } else {
            setShowError(true);
          }
        }}
        validateOnBlur
        enableReinitialize={false}
      >
        {({ values, setFieldValue, handleSubmit, isValid, handleBlur, touched, errors }) => {
          const handleFieldChange = (field, value) => {
            if (field !== 'cobrandId') setIsFormDirty(true);
            setFieldValue(field, value);
          };

          const handleCobrandChange = async (option) => {
            const id = option?.value ?? null;
            setFieldValue('cobrandId', id);
            setIsFormDirty(false);
            setHasSearched(false); // Reset search state when cobrand changes
            if (!id) return;

            const result = await fetchTeams({ url: `/teams/admin-settings?cobrandId=${id}` });
            if (!result.error) {
              const settings = result.data || {};
              setFieldValue('cobrandBaseUrl', settings.cobrandBaseUrl ?? '');
              setFieldValue('cobrandTeamsDescription', settings.cobrandTeamsDescription ?? '');
              setFieldValue('active', settings.active ?? false);
              setFieldValue('teamsIsSetup', settings.teamsIsSetup ?? false);
            } else {
              setShowError(true);
            }
          };

          return (
            <Form style={{ maxWidth: 500, marginBottom: 40 }}>
              <div className="mt-1">
                <label className="default-label mt20">Select Cobrand</label>
                {loadingCobrands ? (
                  <Spinner label="Loading Cobrands..." />
                ) : (
                  <Select
                    components={{ MenuList: CustomMenuList }}
                    options={cobrandsOptions}
                    placeholder="Select Cobrand"
                    name="cobrandId"
                    value={cobrandsOptions?.find((o) => o.value === values.cobrandId) || null}
                    onChange={handleCobrandChange}
                    isSearchable
                    styles={{
                      container: (base) => ({ ...base, width: 300 }),
                    }}
                  />
                )}

                {values.cobrandId && (
                  <>
                    <hr />
                    <div style={{ marginBottom: '16px' }}>
                      <label className="default-label">Teams Is Setup</label>
                      <Text style={{ display: 'block', marginTop: '4px', fontSize: '14px' }}>
                        {values.teamsIsSetup ? 'Yes' : 'No'}
                      </Text>
                    </div>
                    <hr />

                    <Toggle
                      label="Active"
                      inlineLabel
                      checked={!!values.active}
                      onChange={(e, checked) => handleFieldChange('active', !!checked)}
                      disabled={!values.teamsIsSetup && !values.clientTenantId}
                    />
                    <hr />
                    <h3>Wiki Step 7 Fields</h3>
                    {!values.teamsIsSetup && (
                      <>
                        <Text style={{ display: 'block', marginBottom: '16px', color: '#a80000', fontStyle: 'italic' }}>
                          Teams is not set up for this cobrand. Please provide the Client Tenant ID to configure Teams settings.
                        </Text>
                        <TextField
                          label="Client Tenant ID"
                          name="clientTenantId"
                          value={values.clientTenantId || ''}
                          onChange={(e, newValue) => handleFieldChange('clientTenantId', newValue)}
                          onBlur={handleBlur}
                          errorMessage={touched.clientTenantId && errors.clientTenantId}
                          placeholder="Enter the Microsoft Teams Client Tenant ID"
                          required={!values.teamsIsSetup}
                        />
                        <Text variant="small" style={{ display: 'block', marginTop: 4, marginBottom: 16 }}>
                          <strong>This is required when Teams is not yet set up for the selected cobrand.</strong>
                        </Text>

                        <TextField
                          label="Cobrand Id"
                          name="cobrandId"
                          value={values.cobrandId || ''}
                          onChange={(e, newValue) => handleFieldChange('cobrandId', newValue)}
                          onBlur={handleBlur}
                          errorMessage={touched.cobrandId && errors.cobrandId}
                          disabled={true}
                        />
                        <Text variant="small" style={{ display: 'block', marginTop: 4 }}>
                          <strong>Cobrand Id is not setup for Teams for this cobrand yet. Double check the Client Tenant ID in the database (dbo.MSTeamsAppInstallations) is definitely for this cobrand before adding. This can be looked up in Azure Portal and instructions are in the Teams wiki.</strong>
                        </Text>
                      </>
                    )}
                    <TextField
                      label="Cobrand Base URL"
                      name="cobrandBaseUrl"
                      value={values.cobrandBaseUrl || ''}
                      onChange={(e, newValue) => handleFieldChange('cobrandBaseUrl', newValue)}
                      onBlur={handleBlur}
                      errorMessage={touched.cobrandBaseUrl && errors.cobrandBaseUrl}
                      disabled={!values.teamsIsSetup && !values.clientTenantId}
                    />
                    <Text variant="small" style={{ display: 'block', marginTop: 4 }}><strong>If non-SSO cobrand then use the domain masked url (ex: bfu.igrad.com). Otherwise use the SSO url which usually, but not always, follows this format: sso.enrich.org/Shibboleth.sso/Login?entityID={"{dbo.CoBrandSettings.Incommonurl}"}/&target={"{audit.ClientSchoolUser_SSO.LandingPageURL(Minus the query parameters)}"}?targetUrl=</strong>
                    </Text>

                    <TextField
                      label="Cobrand Teams Description"
                      name="cobrandTeamsDescription"
                      value={values.cobrandTeamsDescription || ''}
                      onChange={(e, newValue) => handleFieldChange('cobrandTeamsDescription', newValue)}
                      onBlur={handleBlur}
                      errorMessage={touched.cobrandTeamsDescription && errors.cobrandTeamsDescription}
                      disabled={!values.teamsIsSetup && !values.clientTenantId}
                    />
                    <Text variant="small" style={{ display: 'block', marginTop: 4 }}>
                      <strong>Usually left blank. Enter a value only if you need a white-labeled Teams description.</strong>
                    </Text>
                    <hr />
                  </>
                )}
              </div>

              <SuccessComponent successMessage="Teams settings updated." showSuccess={showSuccess} setShowSuccess={setShowSuccess} />
              <ErrorComponent
                errorMessage="Errors in form or network error."
                showError={showError}
                setShowError={setShowError}
              />

              {values.cobrandId && (
                <div style={{ display: 'flex' }} className="mt-1">
                  <PrimaryButton
                    onClick={() => handleSubmit()}
                    text="Update"
                    disabled={!isValid || !isFormDirty || (!values.teamsIsSetup && !values.clientTenantId)}
                  />
                </div>
              )}

              {values.cobrandId && (
                <div style={{ marginTop: 40 }}>
                  <h3>User Message Logs</h3>
                  <Text variant="small" style={{ display: 'block', marginBottom: 16 }}>
                    Search for messages sent to a specific user by entering their email address.

                  </Text>

                  <div style={{ display: 'flex', gap: 16, alignItems: 'end', marginBottom: 20 }}>
                    <div style={{ minWidth: 250 }}>
                      <TextField
                        label="User Email"
                        value={userEmail}
                        onChange={(e, newValue) => handleEmailChange(newValue)}
                        placeholder="Enter user email address"
                        errorMessage={userEmailError}
                        styles={{ root: { width: 250 } }}
                        type="email"
                        disabled={!values.teamsIsSetup}
                      />
                    </div>

                    <PrimaryButton
                      text="Search"
                      onClick={() => handleSearchUserMessages(values.cobrandId)}
                      disabled={!values.cobrandId || !userEmail || !!userEmailError || loadingUserMessages}
                    />
                  </div>

                  {loadingUserMessages && <Spinner label="Loading user messages..." />}

                  {userMessageLogs && (
                    <div style={{ marginTop: 20 }}>
                      <div style={{
                        backgroundColor: '#f3f2f1',
                        padding: 16,
                        borderRadius: 4,
                        marginBottom: 16,
                        border: '1px solid #edebe9'
                      }}>
                        <h4 style={{ margin: '0 0 8px 0' }}>User Information</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div><strong>Name:</strong> {userMessageLogs.firstName} {userMessageLogs.lastName}</div>
                          <div><strong>Email:</strong> {userMessageLogs.email}</div>
                          <div><strong>Member ID:</strong> {userMessageLogs.memberId}</div>
                          <div><strong>Member AAD ID:</strong> {userMessageLogs.memberAADId}</div>
                        </div>
                      </div>

                      <h4>Messages Sent to User</h4>
                      <DetailsTable
                        items={userMessageLogs.messages || []}
                        columns={userMessageColumns}
                        loading={false}
                        mainColumn="title"
                        detailsListProps={{ selectionMode: SelectionMode.none }}
                        hideSelectionDetails={true}
                        hideFilterField={true}
                      />
                    </div>
                  )}

                  {hasSearched && !loadingUserMessages && (
                    (!userMessageLogs) ||
                    (userMessageLogs && (!userMessageLogs.messages || userMessageLogs.messages.length === 0))
                  ) && (
                      <Text style={{ color: '#a80000', fontStyle: 'italic', marginTop: 16 }}>
                        No messages found for this user.
                      </Text>
                    )}
                </div>
              )}
            </Form>
          );
        }}
      </Formik>

      <div style={{ marginTop: 40 }}>
        <h3>Microsoft Teams Messages Reference List</h3>
        <Text>
          Full list of messages that can be sent to a user who has the Teams app installed, but is not signed up on the site. If all of these messages have been sent to a user who has not signed up on the site, no further messages will be sent.
        </Text>
        <DetailsTable
          items={msTeamsMessages || []}
          columns={messagesColumns}
          loading={loadingMessages}
          mainColumn="title"
          detailsListProps={{ selectionMode: SelectionMode.none }}
          hideSelectionDetails={true}
          hideFilterField={true}
        />
      </div>
    </>
  );
};

export default TeamsSettings;
